import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import SiteContentTab from "./SiteContentTab";

function OrderNoteRow({ orderId, initial, onSave }) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const dirty = value !== initial;
  return (
    <div className="mt-4 pt-4 border-t border-[#e5e5e5]">
      <div className="label-eyebrow mb-2">Admin note</div>
      <div className="flex gap-3 items-start">
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Refund refs, courier info, customer requests…"
          className="flex-1 border border-[#e5e5e5] p-2 text-sm bg-transparent focus:outline-none focus:border-[#111] min-h-[52px]"
          data-testid={`admin-order-note-${orderId}`}
        />
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={async () => {
            setSaving(true);
            await onSave(orderId, value);
            setSaving(false);
          }}
          className="text-xs px-4 py-2 border border-[#111] disabled:opacity-40 hover:bg-[#111] hover:text-white transition-colors self-start"
          data-testid={`admin-order-note-save-${orderId}`}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

const emptyProduct = {
  name: "",
  description: "",
  price: 0,
  category: "loafers",
  image_url: "",
  sizes: ["5", "6", "7", "8", "9", "10", "11", "12"],
  stock: 100,
  material: "Premium synthetic",
  construction: "Hand-stitched",
  origin: "Agra, India",
  color: "",
  colors: [],
};

export default function Admin() {
  const { user, loading } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);

  const loadAll = async () => {
    const [p, o] = await Promise.all([api.get("/products"), api.get("/admin/orders")]);
    setProducts(p.data);
    setOrders(o.data);
  };

  useEffect(() => {
    if (user?.role === "admin") loadAll();
  }, [user]);

  if (loading) return <div className="pt-32 px-6 text-sm text-[#666]">Loading…</div>;
  if (!user || user.role !== "admin") {
    return (
      <div className="pt-32 px-6 text-center">
        <h1 className="font-display text-3xl font-extrabold tracking-tighter">
          Admin access required
        </h1>
        <p className="mt-4 text-[#666] text-sm">Please sign in as admin.</p>
      </div>
    );
  }

  const submitProduct = async (e) => {
    e.preventDefault();
    try {
      const cleanForm = { ...form };
      delete cleanForm._newColor;
      if (editing) {
        await api.put(`/admin/products/${editing}`, { ...cleanForm, price: parseFloat(cleanForm.price) });
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", { ...cleanForm, price: parseFloat(cleanForm.price) });
        toast.success("Product created");
      }
      setEditing(null);
      setForm(emptyProduct);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const editProduct = (p) => {
    setEditing(p.id);
    setForm({ ...emptyProduct, ...p });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const updateOrderStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}`, { status });
    toast.success("Order updated");
    loadAll();
  };

  const resendOrderEmail = async (id, kind) => {
    try {
      await api.post(`/admin/orders/${id}/test-email`, { kind });
      toast.success(`${kind} email sent`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send email");
    }
  };

  const deleteOrder = async (id, shortId) => {
    if (!window.confirm(`Permanently delete order #${shortId}?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success(`Order #${shortId} deleted`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to delete order");
    }
  };

  const saveOrderNote = async (id, note) => {
    try {
      await api.patch(`/admin/orders/${id}`, { admin_notes: note });
      toast.success("Note saved");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save note");
    }
  };

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="label-eyebrow mb-4">Admin</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mb-12">
          Dashboard
        </h1>

        <Tabs defaultValue="products">
          <TabsList className="rounded-none bg-transparent border-b border-[#e5e5e5] w-full justify-start p-0 h-auto">
            <TabsTrigger
              value="products"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#111] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3"
              data-testid="tab-products"
            >
              Products ({products.length})
            </TabsTrigger>
            <TabsTrigger
              value="orders"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#111] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3"
              data-testid="tab-orders"
            >
              Orders ({orders.length})
            </TabsTrigger>
            <TabsTrigger
              value="content"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#111] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3"
              data-testid="tab-content"
            >
              Site Content
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-10">
            <form
              onSubmit={submitProduct}
              className="border border-[#e5e5e5] p-6 mb-10 grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div className="md:col-span-2 label-eyebrow">
                {editing ? "Edit product" : "New product"}
              </div>
              <input
                required
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="border-b border-[#e5e5e5] py-2 focus:outline-none focus:border-[#111] bg-transparent"
                data-testid="admin-product-name"
              />
              <input
                required
                type="number"
                step="0.01"
                placeholder="Price"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="border-b border-[#e5e5e5] py-2 focus:outline-none focus:border-[#111] bg-transparent"
                data-testid="admin-product-price"
              />
              <Select
                value={form.category}
                onValueChange={(v) => setForm({ ...form, category: v })}
              >
                <SelectTrigger className="rounded-none border-0 border-b border-[#e5e5e5] focus:ring-0 focus:border-[#111] bg-transparent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="loafers">Loafers</SelectItem>
                  <SelectItem value="slip-ons">Slip-Ons</SelectItem>
                  <SelectItem value="chelsea">Chelsea</SelectItem>
                  <SelectItem value="derby">Derby</SelectItem>
                  <SelectItem value="monk">Monk</SelectItem>
                  <SelectItem value="heritage">Heritage Comfort</SelectItem>
                  <SelectItem value="wedding">Wedding</SelectItem>
                </SelectContent>
              </Select>
              <input
                required
                placeholder="Image URL"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                className="border-b border-[#e5e5e5] py-2 focus:outline-none focus:border-[#111] bg-transparent"
                data-testid="admin-product-image"
              />
              <input
                placeholder="Material (e.g. Premium synthetic)"
                value={form.material || ""}
                onChange={(e) => setForm({ ...form, material: e.target.value })}
                className="border-b border-[#e5e5e5] py-2 focus:outline-none focus:border-[#111] bg-transparent"
                data-testid="admin-product-material"
              />
              <input
                placeholder="Construction (e.g. Hand-stitched, Blake welt)"
                value={form.construction || ""}
                onChange={(e) => setForm({ ...form, construction: e.target.value })}
                className="border-b border-[#e5e5e5] py-2 focus:outline-none focus:border-[#111] bg-transparent"
                data-testid="admin-product-construction"
              />
              <input
                placeholder="Origin (e.g. Agra, India)"
                value={form.origin || ""}
                onChange={(e) => setForm({ ...form, origin: e.target.value })}
                className="border-b border-[#e5e5e5] py-2 focus:outline-none focus:border-[#111] bg-transparent"
                data-testid="admin-product-origin"
              />
              <input
                placeholder="Colour in photo (e.g. Cognac — the exact shade shown in the image)"
                value={form.color || ""}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="border-b border-[#e5e5e5] py-2 focus:outline-none focus:border-[#111] bg-transparent"
                data-testid="admin-product-color"
              />
              <div className="md:col-span-2 pt-3">
                <div className="label-eyebrow mb-3">Available colours</div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    { name: "Black", swatch: "#111111" },
                    { name: "Brown", swatch: "#4a2f21" },
                    { name: "Cognac", swatch: "#a05a2c" },
                    { name: "Tan", swatch: "#c99a6b" },
                    { name: "Beige", swatch: "#d8c9a3" },
                    { name: "Ivory", swatch: "#f4ecd8" },
                    { name: "White", swatch: "#ffffff" },
                    { name: "Navy", swatch: "#1c2c4a" },
                    { name: "Burgundy", swatch: "#5a1a1f" },
                    { name: "Maroon", swatch: "#7a1e28" },
                    { name: "Grey", swatch: "#7a7a7a" },
                    { name: "Olive", swatch: "#5a5a30" },
                    { name: "Gold", swatch: "#c8a24b" },
                  ].map((c) => {
                    const active = (form.colors || []).includes(c.name);
                    return (
                      <button
                        type="button"
                        key={c.name}
                        onClick={() => {
                          const current = form.colors || [];
                          const next = active
                            ? current.filter((x) => x !== c.name)
                            : [...current, c.name];
                          setForm({ ...form, colors: next });
                        }}
                        className={`flex items-center gap-2 px-3 py-2 border text-xs uppercase tracking-widest transition-colors ${
                          active
                            ? "bg-[#111] text-white border-[#111]"
                            : "bg-white text-[#111] border-[#e5e5e5] hover:border-[#111]"
                        }`}
                        data-testid={`admin-color-toggle-${c.name.toLowerCase()}`}
                      >
                        <span
                          className="inline-block w-4 h-4 rounded-full border border-[#e5e5e5]"
                          style={{ background: c.swatch }}
                        />
                        {c.name}
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add custom colour (e.g. Rose Gold)"
                    value={form._newColor || ""}
                    onChange={(e) => setForm({ ...form, _newColor: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const v = (form._newColor || "").trim();
                        if (!v) return;
                        if ((form.colors || []).includes(v)) return;
                        setForm({ ...form, colors: [...(form.colors || []), v], _newColor: "" });
                      }
                    }}
                    className="flex-1 border border-[#e5e5e5] px-3 py-2 text-sm focus:outline-none focus:border-[#111] bg-transparent"
                    data-testid="admin-color-custom-input"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const v = (form._newColor || "").trim();
                      if (!v) return;
                      if ((form.colors || []).includes(v)) return;
                      setForm({ ...form, colors: [...(form.colors || []), v], _newColor: "" });
                    }}
                    className="bg-[#111] text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#333]"
                    data-testid="admin-color-add-btn"
                  >
                    Add
                  </button>
                </div>
                {(form.colors || []).filter(
                  (c) => !["Black","Brown","Cognac","Tan","Beige","Ivory","White","Navy","Burgundy","Maroon","Grey","Olive","Gold"].includes(c),
                ).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="label-eyebrow self-center">Custom:</span>
                    {(form.colors || [])
                      .filter(
                        (c) => !["Black","Brown","Cognac","Tan","Beige","Ivory","White","Navy","Burgundy","Maroon","Grey","Olive","Gold"].includes(c),
                      )
                      .map((c) => (
                        <button
                          type="button"
                          key={c}
                          onClick={() =>
                            setForm({ ...form, colors: form.colors.filter((x) => x !== c) })
                          }
                          className="bg-[#111] text-white px-3 py-1 text-xs uppercase tracking-widest hover:bg-[#333]"
                          title="Click to remove"
                        >
                          {c} ✕
                        </button>
                      ))}
                  </div>
                )}
                <p className="text-xs text-[#666] mt-3">
                  Click a preset to toggle. Or type a custom colour and press Enter (or Add).
                </p>
              </div>
              <div className="md:col-span-2 pt-3">
                <div className="label-eyebrow mb-3">Available sizes (UK)</div>
                <div className="flex flex-wrap gap-2">
                  {["5", "6", "7", "8", "9", "10", "11", "12"].map((s) => {
                    const active = (form.sizes || []).includes(s);
                    return (
                      <button
                        type="button"
                        key={s}
                        onClick={() => {
                          const current = form.sizes || [];
                          const next = active
                            ? current.filter((x) => x !== s)
                            : [...current, s].sort((a, b) => parseInt(a) - parseInt(b));
                          setForm({ ...form, sizes: next });
                        }}
                        className={`w-14 h-11 border text-sm transition-colors ${
                          active
                            ? "bg-[#111] text-white border-[#111]"
                            : "bg-white text-[#111] border-[#e5e5e5] hover:border-[#111]"
                        }`}
                        data-testid={`admin-size-toggle-${s}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-[#666] mt-2">
                  Click to toggle. Highlighted sizes are available for customers to purchase.
                </p>
              </div>
              <textarea
                required
                placeholder="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="md:col-span-2 border border-[#e5e5e5] p-3 focus:outline-none focus:border-[#111] bg-transparent min-h-[80px]"
                data-testid="admin-product-description"
              />
              <div className="md:col-span-2 flex gap-3">
                <button
                  type="submit"
                  className="bg-[#111] text-white px-6 py-3 text-sm font-medium hover:bg-[#333]"
                  data-testid="admin-product-submit"
                >
                  {editing ? "Update product" : "Create product"}
                </button>
                {editing && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(null);
                      setForm(emptyProduct);
                    }}
                    className="border border-[#e5e5e5] px-6 py-3 text-sm hover:border-[#111]"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>

            <div className="border border-[#e5e5e5]">
              <table className="w-full text-sm">
                <thead className="bg-[#f5f5f5] border-b border-[#e5e5e5]">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium">Product</th>
                    <th className="text-left py-3 px-4 font-medium">Category</th>
                    <th className="text-left py-3 px-4 font-medium">Price</th>
                    <th className="text-right py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-[#e5e5e5] last:border-0"
                      data-testid={`admin-product-row-${p.id}`}
                    >
                      <td className="py-3 px-4 flex items-center gap-3">
                        <img src={p.image_url} alt="" className="w-10 h-12 object-cover" />
                        {p.name}
                      </td>
                      <td className="py-3 px-4 text-[#666]">{p.category}</td>
                      <td className="py-3 px-4">₹{p.price.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right space-x-3">
                        <button
                          onClick={() => editProduct(p)}
                          className="text-xs uppercase tracking-widest hover:opacity-60"
                          data-testid={`admin-edit-${p.id}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteProduct(p.id)}
                          className="text-xs uppercase tracking-widest text-[#D9534F] hover:opacity-60"
                          data-testid={`admin-delete-${p.id}`}
                        >
                          <Trash2 size={14} className="inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="orders" className="mt-10">
            {orders.length === 0 ? (
              <div className="text-sm text-[#666]">No orders yet.</div>
            ) : (
              <div className="space-y-6">
                {orders.map((o) => (
                  <div
                    key={o.id}
                    className="border border-[#e5e5e5] p-6"
                    data-testid={`admin-order-${o.id}`}
                  >
                    <div className="flex flex-wrap justify-between gap-3 items-start">
                      <div>
                        <div className="text-xs text-[#666] uppercase tracking-widest">
                          #{o.id.slice(0, 8).toUpperCase()}
                        </div>
                        <div className="text-sm mt-1">
                          {o.guest_email || o.user_id?.slice(0, 8) || "—"}
                        </div>
                        <div className="text-xs text-[#666] mt-1">
                          {new Date(o.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <div className="font-display font-bold text-lg">
                          ₹{o.total.toFixed(2)}
                        </div>
                        <div className="text-xs uppercase tracking-widest text-[#666]">
                          Payment: {o.payment_status}
                        </div>
                        <button
                          onClick={() => deleteOrder(o.id, o.id.slice(0, 8).toUpperCase())}
                          className="text-xs text-[#a02020] hover:text-[#111] inline-flex items-center gap-1 mt-1"
                          data-testid={`admin-order-delete-${o.id}`}
                          title="Delete order permanently"
                        >
                          <Trash2 size={12} /> Delete order
                        </button>
                      </div>
                    </div>
                    <ul className="mt-4 text-sm text-[#666] space-y-1">
                      {o.items.map((it, idx) => (
                        <li key={idx}>
                          {it.name} · Size {it.size}{it.color ? ` · ${it.color}` : ""} × {it.quantity} = ₹{it.line_total.toFixed(2)}
                        </li>
                      ))}
                    </ul>
                    {(o.shipping || o.guest_info) && (
                      <div className="mt-4 pt-4 border-t border-[#e5e5e5] text-xs text-[#666] space-y-1">
                        <div className="label-eyebrow mb-2 text-[#111]">Ship to</div>
                        {(() => {
                          const s = o.shipping || o.guest_info || {};
                          return (
                            <>
                              <div>{s.name} · {s.phone || "—"}</div>
                              <div>{s.email}</div>
                              <div>{s.address}</div>
                              <div>{s.city}{s.state ? `, ${s.state}` : ""} — {s.postal_code}</div>
                              <div>{s.country || "India"}</div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                    <div className="mt-4 flex items-center gap-3">
                      <span className="label-eyebrow">Status</span>
                      <Select
                        value={o.status}
                        onValueChange={(v) => updateOrderStatus(o.id, v)}
                      >
                        <SelectTrigger
                          className="w-40 rounded-none border-[#e5e5e5]"
                          data-testid={`admin-order-status-${o.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["pending", "processing", "shipped", "delivered", "cancelled"].map(
                            (s) => (
                              <SelectItem key={s} value={s}>
                                {s}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                      <span className="label-eyebrow ml-4">Resend email</span>
                      <Select onValueChange={(v) => resendOrderEmail(o.id, v)}>
                        <SelectTrigger
                          className="w-40 rounded-none border-[#e5e5e5]"
                          data-testid={`admin-order-resend-${o.id}`}
                        >
                          <SelectValue placeholder="Choose…" />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            { v: "confirmed", label: "Order Confirmed" },
                            { v: "processing", label: "In Production" },
                            { v: "shipped", label: "Shipped" },
                            { v: "review", label: "Review Request" },
                          ].map((k) => (
                            <SelectItem key={k.v} value={k.v}>
                              {k.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <OrderNoteRow
                      orderId={o.id}
                      initial={o.admin_notes || ""}
                      onSave={saveOrderNote}
                    />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="content" className="mt-10">
            <SiteContentTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}